/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Status Graph Component
 *
 * Migrated from: lego/page/eos/overview/StatusGraph/StatusGraph.jsx
 *
 * Renders a doughnut chart showing EOS completion status.
 * Supports two rendering modes:
 *   1. Summary info mode (download % / ack %) — when data.infoData is present
 *   2. Doughnut graph mode — when data.graphData is present
 *
 * NOTE: The existing ETMF StatusGraph (etmf/overview/status-graph/StatusGraph.tsx)
 * cannot be reused because:
 *   - ETMF uses a fixed [number, number, number] tuple (3 segments)
 *   - EOS uses dynamic segments (2–4 items) with per-segment labels/colors/statuses
 *   - EOS has a "summary info" mode with no chart at all
 *   - The center text differs (documents count vs percentage)
 */

import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js'
import type { EosStatusGraphProps } from '../types'

// Register Chart.js components
Chart.register(DoughnutController, ArcElement, Tooltip, Legend)

const EosStatusGraph: React.FC<EosStatusGraphProps> = ({ data, filter, isLegacy, onSummaryClick, onStatsClick }) => {
  const { t } = useTranslation()
  const [chart, setChart] = useState<Chart | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const hasGraphData = !!data?.graphData?.length
  const shouldHideLegend = !hasGraphData

  useEffect(() => {
    drawGraph()

    return () => {
      if (chart?.destroy) {
        chart.destroy()
      }
    }
  }, [data])

  const drawGraph = () => {
    if (chart?.destroy) {
      chart.destroy()
    }

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    if (!hasGraphData) return

    const chartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: data!.graphData!.map((item) => item.label),
        datasets: [
          {
            label: '',
            data: data!.graphData!.map((item) => item.data),
            backgroundColor: data!.graphData!.map((item) => item.color)
          }
        ]
      },
      options: {
        plugins: {
          tooltip: {
            enabled: false
          },
          legend: {
            display: false,
            position: 'bottom',
            labels: {
              color: '#333'
            }
          }
        }
      }
    })

    setChart(chartInstance)
  }

  return (
    <div className={`chart-custom-wrapper ${shouldHideLegend ? 'notShowLegendOnHover' : ''}`}>
      {/* Placeholder when no data */}
      {!data && (
        <div className="placeholder-wrapper">
          <div className="placeholder-chart"></div>
          <div className="placeholder-text">{t('ETMF_GRAPHS_NO_DATA')}</div>
        </div>
      )}

      {/* Summary info mode (download % / ack %) */}
      {data?.infoData && (
        <div className="placeholder-wrapper">
          <div className="placeholder-chart"></div>
          <div className="summary-text">
            {data.infoData.map((item, index) => (
              <span key={index} onClick={() => onSummaryClick(item.type, filter)} className="cursor-pointer">
                {item.label}: {item.data}
                {index !== data.infoData!.length - 1 && <hr />}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        {/* Doughnut chart canvas */}
        <canvas ref={canvasRef} id={`canvas-${filter?.type}`} height="150" width="150" />

        {/* Total data in center */}
        <div className="total-docs">
          <span>{data?.totalData?.data}</span>
          {data?.totalData?.inGraphLabel?.toLowerCase()}
        </div>

        {/* Legend with clickable items */}
        <div className="chartjsLegend" style={{ width: isLegacy ? '140px' : '160px' }}>
          <ul className="6-legend">
            {data?.graphData?.map((item, index) => (
              <li key={index} onClick={() => onStatsClick(item.status, filter)}>
                <span className="color-square" style={{ backgroundColor: item.color }}></span>
                <strong>
                  {item.data + ' '}
                  {!isLegacy && <span>{t('EMTF_MD_SITES') + ' '}</span>}
                </strong>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
          <strong>
            {data?.totalData?.label}: {data?.totalData?.data}
          </strong>
        </div>
      </div>
    </div>
  )
}

export default EosStatusGraph
